import React, { useEffect, useState } from "react";
import { CircularProgress, Dialog, Grid, IconButton, Typography } from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { stopUploadingSegmentsToTheCloud } from "../../API/apiDevices";
import { VideoInfo } from "./models/videoInfo";
import Player from "./player";
import { MotionEvent } from "./models/motionEvent";
import { VideoPlayBackMode, ModelAspectRatio, DeviceModel } from "../../Models/devices";
import { MotionEventsHandler } from "./MotionEventsHandler";
import ShareVideoDialog from "./components/ShareVideoDialog";
import FullScreen from "react-fullscreen-crossbrowser";
import { FishEyePlayerModel } from "./models/FishEyePlayerModel";

interface Props {
  serialNumber: string;
  videoTitle: string;
  timezone: string | undefined;
  skipToDateTimeISO?: string;
  onClose: () => void;
  onVideoFinishedPlaying?: (endDateISO: string) => void;
  fatalErrorMessage: string;
  videoInfo: VideoInfo | null;
  isLoading: boolean;
  modelAspectRatio?: ModelAspectRatio;
  shouldDisplayShareButton: boolean;
  shouldDisplayDownloadButton: boolean;
  device?: DeviceModel;
  fishEyePlayerModel?: FishEyePlayerModel;
}

const VideoPlayer = ({
  serialNumber,
  videoTitle,
  timezone,
  skipToDateTimeISO,
  onClose,
  videoInfo,
  onVideoFinishedPlaying,
  fatalErrorMessage,
  isLoading,
  modelAspectRatio,
  shouldDisplayShareButton,
  shouldDisplayDownloadButton,
  device,
  fishEyePlayerModel,
}: Props) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();
  const [localFatalErrorMessage, setLocalFatalErrorMessage] = useState("");
  const [motionEvents, setMotionEvents] = useState<MotionEvent[]>([]);
  const [localIsLoading, setLocalIsLoading] = useState<boolean>(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);

  useEffect(videoInfoWasUpdated, [videoInfo]);

  function videoInfoWasUpdated() {
    if (!videoInfo) {
      return;
    }
    let isMounted = true;
    const load = async () => {
      setLocalIsLoading(true);
      const videoEndDateISOStr = getVideoEndDateISOStr(
        videoInfo.startDateISO,
        videoInfo.totalDurationSeconds
      );
      try {
        const motionEventsResponse = await MotionEventsHandler.getMotionEvents(
          videoInfo.startDateISO,
          videoEndDateISOStr,
          serialNumber
        );
        if (!isMounted) {
          return;
        }
        if (motionEventsResponse.hasErrors) {
          setLocalFatalErrorMessage(t(motionEventsResponse.applicationEvents[0].code));
          return;
        }
        const motionEventsData = motionEventsResponse.payload;
        setMotionEvents(motionEventsData || []);
      } catch (e) {
        setLocalFatalErrorMessage(e);
      } finally {
        setLocalIsLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }

  function getVideoEndDateISOStr(startDateISO: string, durationSeconds: number): string {
    const dateUnix = new Date(startDateISO).getTime() + durationSeconds * 1000;
    return new Date(dateUnix).toISOString();
  }

  const isSDLowPlayback = () => {
    return videoInfo?.playbackMode === VideoPlayBackMode.SDLow;
  };

  const onCloseButtonWasClicked = () => {
    onClose && onClose();
    isSDLowPlayback() && stopUploadingSegmentsToTheCloud(serialNumber);
    setOpen(false);
  };

  const shouldShowError = (): boolean =>
    fatalErrorMessage.length > 0 || localFatalErrorMessage.length > 0;

  const shouldShowPlayer = (): boolean =>
    !localIsLoading &&
    !isLoading &&
    !shouldShowError() &&
    videoInfo !== null &&
    videoInfo !== undefined;

  const shouldShowLoading = (): boolean =>
    (localIsLoading || isLoading || videoInfo === null || videoInfo === undefined) &&
    !shouldShowError();

  return (
    <Dialog open={open} fullScreen={true} maxWidth={false} fullWidth={true}>
      <FullScreen enabled={isFullScreen} onChange={(isEnabled) => setIsFullScreen(isEnabled)}>
        <Grid
          container
          id={"root_fullscreen"}
          alignItems="flex-start"
          style={{
            width: "100vw",
            height: "100vh",
          }}
          test-id="anavio-videoplayer"
        >
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Typography>{videoTitle}</Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={onCloseButtonWasClicked}>
                <CloseOutlined />
              </IconButton>
            </Grid>
          </Grid>
          <Grid container justify="center">
            {shouldShowLoading() && <CircularProgress />}
            {shouldShowError() && (
              <p>
                {localFatalErrorMessage}
                {fatalErrorMessage}
              </p>
            )}
            {shouldShowPlayer() && videoInfo != null && (
              <Player
                modelAspectRatio={modelAspectRatio}
                videoInfo={videoInfo}
                motionEvents={motionEvents}
                startDateIsoStr={videoInfo.startDateISO}
                skipToDateTimeISO={skipToDateTimeISO}
                serialNumber={serialNumber}
                onEnded={onVideoFinishedPlaying}
                isSDLowPlayback={isSDLowPlayback}
                timezone={timezone}
                videoTitle={videoTitle}
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
                openShareDialog={() => setOpenShareDialog(true)}
                shouldDisplayShareButton={shouldDisplayShareButton}
                onError={(e) => setLocalFatalErrorMessage(e)}
                shouldDisplayDownloadButton={shouldDisplayDownloadButton}
                device={device}
                fishEyePlayerModel={fishEyePlayerModel}
              />
            )}
          </Grid>
        </Grid>
        {videoInfo && (
          <ShareVideoDialog
            startDateISOStr={videoInfo.startDateISO}
            durationVideoSeconds={videoInfo.totalDurationSeconds}
            serialNumber={serialNumber}
            title={videoTitle}
            shouldOpen={openShareDialog}
            onClose={() => setOpenShareDialog(false)}
          />
        )}
      </FullScreen>
    </Dialog>
  );
};
export default VideoPlayer;
